import { z } from "zod";

/** A single [x, y] coordinate, normalized to 0–1. */
// const PointSchema = z.tuple([z.number(), z.number()]);
const PointSchema = z.array(z.number());

/**
 * Base detection box shared by empty areas and persons.
 * `points` is left as a variable-length array since corner ordering
 * isn't consistent across sources — tighten to `.length(4)` if guaranteed.
 */
const DetectionBoxSchema = z.object({
  points: z.array(PointSchema),
  confidence: z.number(),
  class_index: z.number().int(),
  // Grouped query images: which view the box was drawn on. Absent = primary.
  image_id: z.string().nullish(),
});

const EmptyAreaSchema = DetectionBoxSchema.extend({
  id: z.uuid(),
});

const EmptyAreaSourceSchema = z.enum(["auto", "human"]);

const BoxCategorySchema = z.enum(["oos_confirmed", "empty_area"]);

const ProductSelectionSchema = z.object({
  empty_area_index: z.number().int(),
  product_ids: z.array(z.string()),
  // Plain string, not .uuid() — observed as "" when no selection is made.
  planogram_id: z.string(),
  bay_index: z.number().int(),
});

const OutOfStockConfirmationSchema = z.object({
  query_empty_area_index: z.number().int(),
  confirmation_product_id: z.string(),
  confirmation_product_ids: z.array(z.string()),
  // Always null in sample data — types below are inferred, adjust if needed.
  override_planogram_id: z.string().nullable(),
  identified_product_id: z.string().nullable(),
  // Primary reference product-detection index (single). `product_indices` holds
  // the full list, mirroring confirmation_product_id / confirmation_product_ids.
  product_index: z.number().int(),
  product_indices: z.array(z.number().int()),
  reference_indices: z.array(z.number().int()).nullable(),
});

const MetadataSchema = z.object({
  image_quality_ok: z.boolean(),
  // Marks this submission as Coverage Beta's — the backend's daily coverage-QA
  // cron selects on it to route only Beta submissions into Beta QA. Optional
  // since older/other payloads (and this schema is Beta-only today) may omit it.
  ui: z.literal("coverage_beta").optional(),
  // Active-time analytics (ms), recorded at submit. Optional so older/other
  // payloads without instrumentation still validate. See CoverageBetaActivityTracker.
  total_duration: z.number().optional(),
  active_duration: z.number().optional(),
  idle_duration: z.number().optional(),
  time_to_first_action: z.number().nullable().optional(),
  // Localization efficiency: per-area active time + how much the active area moved.
  area_active_ms: z.record(z.string(), z.number()).optional(),
  area_switches: z.number().optional(),
  // Per-action hotkey-vs-click usage counts (measures hotkey adoption).
  interaction_counts: z
    .record(z.string(), z.object({ hotkey: z.number(), click: z.number() }))
    .optional(),
  // Perf validation: was the next task fully prefetched (instant flip) at submit,
  // and was there a next task to prefetch at all.
  prefetch_ready: z.boolean().optional(),
  next_task_available: z.boolean().optional(),
  // Confirm-flow / rework event counts (confirm_pick, confirm_no_match,
  // confirm_cancel, confirm_reopen, product_removed, blocked_by_confirm).
  event_counts: z.record(z.string(), z.number()).optional(),
  // Areas pre-filled from another task's labels (SAM2 propagation) and what
  // the labeler did with them — measures whether propagation can be trusted.
  propagated: z
    .object({
      shown: z.number(),
      accepted: z.number(),
      edited: z.number(),
      deleted: z.number(),
    })
    .optional(),
  // Per pre-filled area (keyed by its empty_areas id): the guess it came from
  // and what the labeler changed, so lebron doesn't re-track an area onto
  // frames that already have it.
  propagated_areas: z
    .record(
      z.string(),
      z.object({
        source_task_id: z.string(),
        source_area_id: z.string(),
        box_edited: z.boolean(),
        products_edited: z.boolean(),
      }),
    )
    .optional(),
});

export const CompleteTaskResultSchema = z.object({
  deleted_empty_areas: z.array(z.number().int()),
  empty_areas: z.array(EmptyAreaSchema),
  empty_areas_source: z.array(EmptyAreaSourceSchema),
  empty_area_to_aisle: z.array(z.number().int()),
  unblurred_persons: z.array(DetectionBoxSchema),
  product_selections: z.array(ProductSelectionSchema),
  box_categories: z.array(BoxCategorySchema),
  out_of_stock_confirmations: z.array(OutOfStockConfirmationSchema),
  // Slot/aisle index (as string key) → replacement planogram id. Only present
  // when the labeler replaced or added a planogram.
  override_planogram_ids: z.record(z.string(), z.string()).optional(),
  metadata: MetadataSchema,
  testing: z.boolean(),
});

export const CompleteTaskPayloadSchema = z.object({
  result: CompleteTaskResultSchema,
});

export type CompleteTaskPayloadSchemaType = z.infer<
  typeof CompleteTaskPayloadSchema
>;
